/**
 * Effect types in the UI — the vocabulary from packages/shared/effectTypes.js
 * plus the Finnish words participants actually see.
 *
 * The keys are stored and sent; the labels are display only. When the type
 * picker lands (specs/impact-path-v2-prd.md §3) these move into the locale
 * files like every other string — they live here while the set is still moving.
 */
export {
  EFFECT_TYPES,
  EFFECT_TYPE_KEYS,
  DEFAULT_EFFECT_TYPE,
  LEGACY_EFFECT_TYPES,
  normalizeEffectType,
  isCausalEffect,
  effectPolarity,
} from '../../../../packages/shared/effectTypes.js';

/** Finnish labels, in the order they should appear in a picker. */
export const EFFECT_TYPE_LABELS_FI = {
  leads_to: 'johtaa',
  enables: 'mahdollistaa',
  blocks: 'estää',
  reinforces: 'vahvistaa',
  weakens: 'heikentää',
  nullifies: 'kumoaa',
  requires: 'edellyttää',
  delays: 'viivästyttää',
  relates_to: 'liittyy',
  is_same: 'sama asia',
};

/**
 * Edge colour per type. The map should read as a system at a glance: what
 * amplifies, what dampens, what merely enables. Reds dampen, greens amplify,
 * blue enables, and the non-causal `relates_to` stays visually quiet so it does
 * not compete with the causal spine.
 */
export const EFFECT_TYPE_COLORS = {
  leads_to: '#7d8fa3',
  reinforces: '#4CAF50',
  weakens: '#E5804D',
  // A deeper red than weakening, short of the blocker's: cancelling something
  // out is not stopping it from happening.
  nullifies: '#C2445B',
  enables: '#42A5F5',
  blocks: '#EF5350',
  requires: '#AB7DF6',
  delays: '#D4A72C',
  relates_to: 'rgba(150, 160, 175, 0.4)',
  // Not a relation but an identity claim, so it borrows neither the amplifying
  // greens nor the dampening reds.
  is_same: '#8E9BB3',
};

/** Colour for a stored value, or null when the edge is untyped (draw as before). */
export function effectColor(value) {
  const key = normalizeEffectTypeSafe(value);
  return key ? EFFECT_TYPE_COLORS[key] : null;
}

// Local guard so this file works with raw stored values (including legacy ones)
// without importing the normalizer twice.
function normalizeEffectTypeSafe(value) {
  if (typeof value !== 'string') return null;
  return EFFECT_TYPE_COLORS[value] ? value : null;
}

/**
 * Icons for the picker. Lucide names, resolved by the component — the config
 * stays free of React imports.
 */
export const EFFECT_TYPE_ICONS = {
  leads_to: 'ArrowRight',
  enables: 'KeyRound',
  blocks: 'Ban',
  reinforces: 'TrendingUp',
  weakens: 'TrendingDown',
  nullifies: 'CircleSlash',
  delays: 'Hourglass',
  relates_to: 'Link2',
  requires: 'CornerLeftUp',
  is_same: 'Equal',
};

/**
 * What the picker offers, in order.
 *
 * Two absences are deliberate. `requires` reads backwards on a directed arrow
 * ("A edellyttää B" while the arrow points A→B) and `enables` says the same
 * thing the right way round. `blocks` is gone because a block is not a kind of
 * consequence: choosing it created a *node* saying "this does not happen",
 * which is a strange thing to have on a map of what does. Blockers are their
 * own objects on the connector — see specs/impact-path-v2-prd.md §4.
 *
 * Both stay valid stored values so existing rows still normalize.
 */
export const PICKABLE_EFFECT_TYPES = [
  'leads_to',
  'enables',
  'reinforces',
  'weakens',
  'nullifies',
  'delays',
  'relates_to',
];

/**
 * `is_same` is deliberately not in that list. It is not a kind of influence but
 * a claim that there is no influence to describe — the two cards are one thing
 * written twice — so it is picked where the other whole-claim choices are, on
 * the drop (impactmap/components/LinkKindPopover.jsx), not among the verbs.
 */
